/**
 * Where the API lives.
 *
 * Kept in its own module — with no `import.meta.env` and no browser globals — so
 * the Node scripts under `scripts/` can share it instead of each re-deriving the
 * URL. The backend serves business routes only under `/api`; `/health` is the
 * single exception and lives outside it.
 */

/** Backend used by `npm run dev` when nothing is configured. */
export const LOCAL_API_URL = "http://localhost:5000/api";

/** Backend a production build uses when `VITE_API_URL` is missing. */
export const PRODUCTION_API_URL = "https://swastik-salon.onrender.com/api";

/**
 * Normalises a configured URL into the base every request hangs off.
 *
 * `VITE_API_URL` may be written either way, because both are natural things to
 * paste and only one of them is obviously right:
 *   https://host              -> https://host/api
 *   https://host/api          -> https://host/api
 *   https://host/api/         -> https://host/api
 *
 * Trailing slashes are stripped first, otherwise the first path segment of a
 * request such as `/clients` would be swallowed and the request would land on
 * `/api/clients` from the wrong place.
 *
 * Anything that is not an absolute http(s) URL (empty, "/", "/api", a bare
 * host with no scheme, ...) is rejected: a relative base would make the browser
 * call the frontend origin itself, which answers 404 for every API path.
 *
 * @returns {string | null} null when nothing usable was configured.
 */
export function resolveApiBaseUrl(value) {
  let trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  // Reject relative / scheme-less values; only http(s) origins are usable.
  if (!/^https?:\/\//i.test(trimmed)) return null;
  // Strip every trailing slash: "https://host///" -> "https://host".
  trimmed = trimmed.replace(/\/+$/, "");
  if (/\/api$/i.test(trimmed)) return trimmed;
  return `${trimmed}/api`;
}

/** The base URL without its `/api` suffix, for display. */
export function apiOriginOf(baseUrl) {
  return String(baseUrl ?? "").replace(/\/api\/?$/i, "");
}
